const apiUrl = process.env.REACT_APP_API_URL;

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export type HotDealListType = "hot" | "watch";

export interface HotDeal {
  id: number;
  ticker: string;
  in_hot_list: boolean;
  in_watch_list: boolean;
  issuer_name: string | null;
  deal_type: string | null;
  fo_type: string | null;
  sector: string | null;
  region: string | null;
  deal_size: number | string | null;
  issue_price: number | string | null;
  price_range: string | null;
  pricing_range_min: number | string | null;
  pricing_range_max: number | string | null;
  target_price: number | string | null;
  limit_price: number | string | null;
  stop_loss: number | string | null;
  pricing_date: string | null;
  deal_status: string | null;
  /** Computed server-side from the live DealUnifiedData status === "Issued". */
  is_listed?: boolean;
  /** Latest daily close from market_technical_indicators. */
  last_close?: number | null;
  /** Trading date of `last_close` (ISO "YYYY-MM-DD"). */
  last_close_date?: string | null;
  first_day_high?: number | null;
  first_day_close?: number | null;
  target_hit?: boolean;
  stop_hit?: boolean;
  limit_hit?: boolean;
  alert?: boolean;
  added_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Map a Deal Calendar table row to the Hot Deal POST payload. */
function toHotDealPayload(row: Record<string, any>): Record<string, any> {
  return {
    ticker: row.ticker,
    issuer_name: row.issuer_name ?? row.company_name ?? "",
    deal_type: row.deal_type ?? "",
    fo_type: row.fo_type ?? "",
    sector: row.sector ?? "",
    region: row.region ?? "",
    deal_size: row.deal_size ?? null,
    issue_price: row.issue_price ?? null,
    price_range: row.price_range ?? "",
    pricing_range_min: row.pricing_range_min ?? null,
    pricing_range_max: row.pricing_range_max ?? null,
    pricing_date: row.pricing_date ?? "",
    deal_status: row.deal_status ?? "",
    unique_deal_id: row.unique_deal_id ?? "",
    deal_id: row.deal_id ?? "",
  };
}

export async function fetchHotDeals(): Promise<HotDeal[]> {
  const res = await fetch(`${apiUrl}/api/hot_deals/`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to fetch hot deals");
  return res.json();
}

export async function addHotDeal(
  row: Record<string, any>,
  listType: HotDealListType = "hot"
): Promise<{ created: boolean; data: HotDeal }> {
  const res = await fetch(`${apiUrl}/api/hot_deals/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ ...toHotDealPayload(row), list_type: listType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to add hot deal");
  }
  return res.json();
}

export async function updateHotDeal(
  id: number,
  fields: {
    target_price?: number | null;
    limit_price?: number | null;
    stop_loss?: number | null;
    issue_price?: number | null;
    first_day_high?: number | null;
    first_day_close?: number | null;
  }
): Promise<HotDeal> {
  const res = await fetch(`${apiUrl}/api/hot_deals/`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify({ id, ...fields }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to update hot deal");
  }
  return res.json();
}

/**
 * Remove a deal from one list. Omit `listType` to delete the deal entirely.
 * When a deal belongs to both lists, removing it from one leaves the other.
 */
export async function removeHotDeal(
  id: number,
  listType?: HotDealListType
): Promise<void> {
  const qs = listType ? `?id=${id}&list_type=${listType}` : `?id=${id}`;
  const res = await fetch(`${apiUrl}/api/hot_deals/${qs}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to remove hot deal");
}

// ── Access + admin (dynamic allow-list) ──

export interface HotDealsAccess {
  username: string;
  has_access: boolean;
  is_admin: boolean;
  alert_count: number;
}

export async function fetchHotDealsAccess(): Promise<HotDealsAccess> {
  const res = await fetch(`${apiUrl}/api/hot_deals/access/`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch hot deals access");
  return res.json();
}

// ── Ticker search over DealUnifiedData (for the 'add a ticker' box) ──

export interface DealSearchResult {
  ticker: string;
  issuer_name: string | null;
  deal_type: string | null;
  fo_type: string | null;
  sector: string | null;
  region: string | null;
  deal_size: number | null;
  issue_price: number | null;
  pricing_range_min: number | null;
  pricing_range_max: number | null;
  pricing_date: string | null;
  deal_status: string | null;
  unique_deal_id: string | null;
  deal_id: string | null;
}

export async function searchDeals(
  q: string,
  dealType?: string
): Promise<DealSearchResult[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (dealType) params.set("deal_type", dealType);
  const res = await fetch(
    `${apiUrl}/api/hot_deals/search/?${params.toString()}`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error("Failed to search deals");
  const data = await res.json();
  return data.results || [];
}

export interface HotDealAdminUser {
  id: number;
  username: string;
  email: string;
  is_superuser: boolean;
  is_staff: boolean;
  has_access: boolean;
  is_permanent: boolean;
}

export async function fetchHotDealUsers(): Promise<{
  users: HotDealAdminUser[];
  active_count: number;
}> {
  const res = await fetch(`${apiUrl}/api/hot_deals/admin/users/`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to fetch users");
  }
  return res.json();
}

export async function grantHotDealAccess(userId: number): Promise<void> {
  const res = await fetch(`${apiUrl}/api/hot_deals/admin/users/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to grant access");
  }
}

export async function revokeHotDealAccess(userId: number): Promise<void> {
  const res = await fetch(`${apiUrl}/api/hot_deals/admin/users/?user_id=${userId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to revoke access");
  }
}
